<template>
  <div v-if="showContent && !!personalData">
    <div class="regulatory-update-personal-data">
      <HeadingDisplay
        title="Dados pessoais"
        head="Revisão de dados"
        class="regulatory-update-personal-data__heading"
      />
      <InputText
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.fullName"
        label="Nome completo"
        readonly
      />
      <div
        v-if="enableRegulatoryUpdateBureau"
        class="regulatory-update-personal-data__column-birth"
      >
        <InputText
          v-model="regulatoryUpdateStore.personalProfile.birthDate"
          label="Data de nascimento"
          readonly
        />
        <InputSelect
          v-model="regulatoryUpdateStore.personalProfile.sex"
          label="Sexo"
          :options="genderOptions"
          disabled
        />
      </div>
      <InputSelect
        v-model="personalData.maritalStatus"
        label="Estado civil"
        required
        :options="maritalStatusList"
        :helper-text="getErrorMessage('maritalStatus')"
        :status-type="getInputStatus('maritalStatus')"
        @change="blurMaritalStatus"
      />
      <InputText
        v-if="showPartnerNameField"
        v-model="personalData.partnerName"
        label="Nome da(o) companheira(o)"
        :helper-text="getErrorMessage('partnerName')"
        :status="getInputStatus('partnerName')"
        @focus="removeError('partnerName')"
      />
      <InputText
        v-if="enablePartnerCpf"
        v-model="personalData.partnerCpf"
        name="partnerCpf"
        label="CPF da(o) companheira(o)"
        mask="###.###.###-##"
        :helper-text="getErrorMessage('partnerCpf')"
        :status="getInputStatus('partnerCpf')"
        @focus="removeError('partnerCpf')"
      />
      <InputText
        v-if="enablePartnerRne"
        v-model="personalData.partnerForeignRne"
        name="partnerForeignRne"
        label="RNE da(o) companheira(o)"
        mask="X######-X"
        :helper-text="getErrorMessage('partnerForeignRne')"
        :status="getInputStatus('partnerForeignRne')"
        @focus="removeError('partnerForeignRne')"
      />
      <InputSelect
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.nationality"
        label="Nacionalidade"
        :options="nationalityOptions"
        disabled
      />
      <InputText
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.cpf"
        label="CPF"
        readonly
      />
      <InputSelect
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.birthState"
        label="Estado de nascimento"
        :options="birthStateOptions"
        disabled
      />
      <InputText
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.birthCity"
        label="Cidade de nascimento"
        disabled
      />
      <InputText
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.motherName"
        label="Nome completo da mãe"
        readonly
      />
      <InputSelect
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.education"
        label="Escolaridade"
        :options="educationOptions"
      />
      <ProfessionInput
        class="regulatory-update-personal-data__profession"
        :profession="personalData.profession.profession"
        :cbo="personalData.profession.cbo"
        :error="getErrorMessage('profession')"
        @on-select="setProfession"
      />
      <div class="regulatory-update-personal-data__column">
        <Checkbox
          v-model="personalData.hasProfession"
          class="regulatory-update-personal-data__has-profession"
          @change="blurHasProfession"
        >
          Estou trabalhando
        </Checkbox>
        <InputText
          v-if="personalData.hasProfession"
          v-model="personalData.workOrganization"
          class="regulatory-update-personal-data__work-organization"
          label="Empresa que trabalha"
          :helper-text="getErrorMessage('workOrganization')"
          :status="getInputStatus('workOrganization')"
          @focus="removeError('workOrganization')"
        />
      </div>
      <InputText
        v-if="personalData.hasProfession"
        v-model="personalData.workOrganizationCnpj"
        name="workOrganizationCnpj"
        label="CNPJ da empresa"
        mask="##.###.###/####-##"
        :helper-text="getErrorMessage('workOrganizationCnpj')"
        :status="getInputStatus('workOrganizationCnpj')"
        @focus="removeError('workOrganizationCnpj')"
      />
      <InputText
        v-if="enableRegulatoryUpdateBureau"
        v-model="regulatoryUpdateStore.personalProfile.mobile"
        label="Celular"
        readonly
      />
      <div
        v-if="enableRegulatoryUpdateBureau"
        class="regulatory-update-personal-data__pep"
      >
        <ToggleSwitch v-model="regulatoryUpdateStore.personalProfile.isExposed">
          <BaseText size="p3" :color="NebraskaColors.elementPrimary"
            >Pessoa Politicamente Exposta</BaseText
          >
        </ToggleSwitch>
        <BaseText size="p4">
          A pessoa tem relação familiar com pessoas em empregos, cargos ou
          funções públicas relevantes, ou outros, conforme Anexo A da Resolução
          CVM Nº 50, de 31 de agosto de 2021.
        </BaseText>
      </div>
      <ButtonContainer class="regulatory-update-personal-data__footer">
        <template #button-primary>
          <ButtonPrimary
            :loading="isButtonLoading"
            size="large"
            label="Continuar"
            @click="handleButtonClick"
          />
        </template>
      </ButtonContainer>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import {
  HeadingDisplay,
  InputSelect,
  InputText,
  Checkbox,
  ToggleSwitch,
  BaseText,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { getMaritalStatusList } from '@/modules/account/services/resources';
import {
  MaritalStatus,
  Profession
} from '@/modules/account/services/resources/types';
import { ProfessionInput } from '@/modules/common/components/ProfessionInput';
import { YupValidationErrors } from '@/modules/common/helpers/yup-validation';

import {
  RegulatoryUpdatePersonalData as IRegulatoryUpdatePersonalData,
  InputStatusType,
  RegulatoryUpdateCustomerPersonalProfile
} from '../../types';
import {
  getPersonalData,
  savePersonalData,
  getStates,
  getCustomerInfo
} from '../../services';
import { isMarried } from '../../helpers/is-married';
import { parseApiErrorToScreenError } from '../../helpers/parse-api-error-to-screen-error';
import yupValidation from './validate';
import {
  trackEvent,
  trackContinue,
  trackError,
  trackView
} from './track-events';
import { EventKnownProperties } from '@warrenbrasil/web-analytics';
import { useRegulatoryUpdateStore } from '@/modules/account/views/RegulatoryUpdate/store/regulatoryUpdateStore';
import {
  genderOptions,
  nationalityOptions,
  educationOptions
} from '@/modules/account/views/RegulatoryUpdate/resources';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

@Component({
  components: {
    HeadingDisplay,
    InputSelect,
    InputText,
    ProfessionInput,
    Checkbox,
    BaseText,
    ToggleSwitch,
    ButtonContainer,
    ButtonPrimary
  }
})
export default class RegulatoryUpdatePersonalData extends Vue {
  @Prop({ type: Boolean, required: true })
  public showContent!: boolean;

  @Prop({ type: String, default: 'regulatoryUpdatePersonalData' })
  public cameFrom!: string;

  @Emit()
  private setIsLoading(_isLoading: boolean) {}

  @Emit()
  private setHasError(_hasError: boolean) {}

  @Emit()
  private goToNextPage() {}

  @Emit()
  private notifyError(_message?: string) {}

  @Emit()
  private changeTracker(
    _newTracker: (remainingProps: EventKnownProperties) => void
  ) {}

  private readonly regulatoryUpdateStore = useRegulatoryUpdateStore();
  public readonly genderOptions = genderOptions;
  public readonly nationalityOptions = nationalityOptions;
  public readonly NebraskaColors = NebraskaColors;
  public personalData: IRegulatoryUpdatePersonalData | null = null;

  public maritalStatusList: MaritalStatus[] = [];
  public educationOptions = educationOptions;
  public birthStateOptions: { label: string; value: string }[] = [];
  public isButtonLoading = false;

  public errors: YupValidationErrors = {};

  mounted() {
    this.fetchPersonalData();
    this.changeTracker(trackEvent);
  }

  private get enablePartnerCpf() {
    switch (this.personalData!.maritalStatus) {
      case 'casado-brasileiro-nato':
      case 'casado-brasileiro-naturalizado':
      case 'uniao-estavel':
        return true;
      default:
        return false;
    }
  }

  private get enablePartnerRne() {
    return this.personalData!.maritalStatus === 'casado-estrangeiro';
  }

  private async fetchPersonalData() {
    this.setIsLoading(true);
    this.setHasError(false);

    try {
      const [personalData, maritalStatusList, birthStateOptions] =
        await Promise.all([
          getPersonalData(),
          getMaritalStatusList(),
          getStates()
        ]);

      trackView();

      if (this.enableRegulatoryUpdateBureau) {
        const { address, investmentProfile, personalProfile } =
          await getCustomerInfo();

        this.regulatoryUpdateStore.setPersonalProfile(personalProfile);
        this.regulatoryUpdateStore.setAddress(address);
        this.regulatoryUpdateStore.setInvestmentProfile(investmentProfile);

        this.personalData =
          personalProfile as unknown as IRegulatoryUpdatePersonalData;

        this.setProfession({
          profession: personalProfile!.profession,
          cbo: personalProfile!.professionId,
          sinacor: null
        });
      } else {
        this.personalData = personalData;
      }

      this.maritalStatusList = maritalStatusList;
      this.birthStateOptions = birthStateOptions;
      this.blurMaritalStatus();
      this.blurHasProfession();
    } catch (_e) {
      this.setHasError(true);
    }

    this.setIsLoading(false);
  }

  public get showPartnerNameField() {
    return isMarried(this.personalData!.maritalStatus);
  }

  public setProfession({ profession, cbo, sinacor }: Profession) {
    this.personalData!.profession = { profession, cbo, sinacor };
    this.removeError('profession');
  }

  public getInputStatus(key: string) {
    return this.errors[key] ? InputStatusType.Error : InputStatusType.Default;
  }

  public getErrorMessage(key: string) {
    return this.errors[key] ? this.errors[key][0] : '';
  }

  public removeError(key: string) {
    delete this.errors[key];
  }

  public blurMaritalStatus() {
    if (!isMarried(this.personalData!.maritalStatus)) {
      this.personalData!.partnerName = '';
      this.personalData!.partnerCpf = '';
      this.personalData!.partnerForeignRne = '';
    }

    if (this.enablePartnerCpf) {
      this.personalData!.partnerForeignRne = '';
    }

    if (this.enablePartnerRne) {
      this.personalData!.partnerCpf = '';
    }
  }

  public blurHasProfession() {
    if (!this.personalData!.hasProfession) {
      this.personalData!.workOrganization = '';
      this.personalData!.workOrganizationCnpj = '';
    }
  }

  public async handleButtonClick() {
    await this.validateErrors();

    if (yupValidation.isValid()) {
      this.isButtonLoading = true;
      try {
        if (this.enableRegulatoryUpdateBureau) {
          this.regulatoryUpdateStore.setPersonalProfile(
            this.personalDataAsRegulatoryProfile
          );

          this.updateProfessionalData();
        } else {
          await savePersonalData(this.personalData!);
        }

        trackContinue();
        this.goToNextPage();
      } catch (e: any) {
        if (e?.error?.message?.value === 'InvalidInput') {
          this.errors = parseApiErrorToScreenError(e.error.locations);
        } else if (e?.error?.message?.value === 'ExpiredPassword') {
          this.$router.push({
            name: 'regulatoryUpdateVerifyPassword',
            query: { cameFrom: this.cameFrom }
          });
        } else {
          trackError(e?.error?.message?.formatted);
          this.notifyError(e?.error?.message?.formatted);
        }
      }
      this.isButtonLoading = false;
    }

    Object.values(this.errors).forEach((v: string[]) => trackError(v[0]));
  }

  private async validateErrors() {
    try {
      await yupValidation.validate(this.personalData);
    } catch (_e) {}

    this.errors = yupValidation.getAllErrors();
  }

  get enableRegulatoryUpdateBureau() {
    return (
      this.cameFrom === 'regulatoryUpdatePersonalData' &&
      FeatureFlagsModule.isEnabled(FeatureFlags.REGULATORY_UPDATE_BUREAU)
    );
  }

  get personalDataAsRegulatoryProfile() {
    return this
      .personalData as unknown as RegulatoryUpdateCustomerPersonalProfile;
  }

  private updateProfessionalData() {
    this.regulatoryUpdateStore.updatePersonalProfileField(
      'profession',
      this.personalData!.profession.profession
    );

    this.regulatoryUpdateStore.updatePersonalProfileField(
      'professionId',
      this.personalData!.profession.cbo
    );
  }
}
</script>

<style lang="less" scoped>
.regulatory-update-personal-data {
  padding-bottom: @size-spacing-x1800;
  display: flex;
  flex-direction: column;
  gap: @size-spacing-x500;

  &__heading {
    margin-bottom: @size-spacing-x800;
  }

  &__column,
  &__column-birth {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    @media @mobileDown {
      flex-direction: column;
      gap: @size-spacing-x500;
    }
  }

  &__column-birth {
    gap: @size-spacing-x500;

    * {
      width: 100%;
    }
  }

  &__profession {
    margin-top: -9px;
    margin-bottom: -24px;
  }

  &__has-profession {
    margin: 8px 0;

    @media @mobileDown {
      margin: 0;
    }
  }

  &__work-organization {
    width: 50%;

    @media @mobileDown {
      width: 100%;
    }
  }

  &__footer {
    position: fixed;
    left: 0;
    bottom: 0;
    background-color: @background-secondary;
    z-index: 1;
  }
}
</style>
